import { OperationVariables } from "@apollo/client";
import { RefetchFunction } from "@apollo/client/react/hooks/useSuspenseQuery";
import { Question_Type, useConfirmOrRejectQuestionMutation } from "@generated/graphql";
import { Chip, Stack, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "components/form/CheckboxControl/components/Checkbox";
import TextFormatter from "components/TextFormatter";
import { QUESTION_TYPE_TITLE } from "constants/global";
import { QUESTION_LANGUAGE_TITLE } from "constants/language";
import { ConfirmModalContext } from "context/confirmModalContext";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { confirmationColor, formatNumber, gradeColor } from "helpers/index";
import LabelBox from "pages/Skill/view/components/QuestionItem/components/LabelBox";
import ValueBox from "pages/Skill/view/components/QuestionItem/components/ValueBox";
import { QuestionItemType } from "pages/Skill/view/types";
import qs from "qs";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { palette } from "theme";
import ActionButtons from "./ActionButtons";

interface QuestionItemProps {
  question: QuestionItemType;
  refetch?: RefetchFunction<unknown, OperationVariables> | (() => void);
  navigateToQuestionSearch?: string;
  showConfirmationInfo?: boolean;
  selectionMode?: boolean;
}

const QuestionItem = ({
  question,
  refetch,
  navigateToQuestionSearch,
  showConfirmationInfo = false,
  selectionMode = false,
}: QuestionItemProps) => {
  const [confirmOrRejectQuestion, { loading: confirmRejectLoading }] = useConfirmOrRejectQuestionMutation();
  const { handleOpen } = useContext(ConfirmModalContext);
  const { addToast } = useContext(ToastContext);
  const questionRef = useRef<HTMLDivElement>(null);
  const [referenceAnswerOpened, setReferenceAnswerOpened] = useState(false);
  const [criterionOpened, setСriterionOpened] = useState(false);
  const referenceAnswerButtonText = referenceAnswerOpened ? "Скрыть эталонный ответ" : "Показать эталонный ответ";
  const criterionButtonText = criterionOpened ? "Скрыть критерии" : "Показать критерии";
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => qs.parse(searchParams.toString()), [searchParams]) as {
    questionId?: string;
  };
  const { questionId: questionIdParam } = params;

  const { type } = question;
  const isConfirm = question.confirmation;

  const onConfirmOrReject = () =>
    confirmOrRejectQuestion({
      variables: {
        questionId: question.id,
      },
    });

  const onConfirm = () => {
    handleOpen({
      title: isConfirm ? "Вы уверены, что хотите отклонить вопрос?" : "Вы уверены, что хотите подтвердить вопрос?",
      actionButtonText: isConfirm ? "Отклонить" : "Подтвердить",
      loading: confirmRejectLoading,
      handleClick: async () =>
        onConfirmOrReject()
          .then(() => {
            if (refetch) refetch();
            addToast({ type: ToastTypeEnum.SUCCESS });
          })
          .catch((e) => {
            parseApolloError(e, { addToast });
          }),
    });
  };

  useEffect(() => {
    if (questionRef && question.id === questionIdParam) {
      // setTimeout необходим из-за использования setTimeout внутри backNavigate из NavigationHistoryProvider,
      // с помощью которого осуществляется возврат на эту страницу из страницы редактирования вопроса
      setTimeout(() => {
        questionRef.current?.scrollIntoView({ behavior: "smooth" });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { questionId: _, ...otherParams } = params;
        setSearchParams(qs.stringify(otherParams), { replace: true });
      });
    }
  }, [questionRef]);

  const questionParts = question.parts;
  const questionCriterion = question.criterion;

  return (
    <Box
      sx={{ position: "relative" }}
      ref={questionRef}>
      <ActionButtons
        question={question}
        refetch={refetch}
        navigateToQuestionSearch={navigateToQuestionSearch}
        disabled={selectionMode}
      />
      <Stack
        spacing={1.5}
        sx={{ py: 2 }}>
        <Stack
          spacing={2}
          sx={{ pr: 3 }}
          alignItems='flex-start'>
          <Stack
            direction='row'
            justifyContent='space-between'
            sx={{ width: "100%", paddingRight: 2 }}>
            <Stack
              direction='row'
              spacing={2}>
              {question.grade && (
                <Chip
                  size='small'
                  label={question.grade}
                  sx={{
                    backgroundColor: `${gradeColor(question.grade)}.light`,
                    color: `${gradeColor(question.grade)}.contrast`,
                  }}
                />
              )}
              <Stack
                direction='row'
                spacing={1.5}
                alignItems='center'>
                <Box
                  sx={{
                    borderRadius: 2,
                    backgroundColor: palette.blue[100],
                    px: 1.5,
                    py: 1,
                  }}>
                  <Typography
                    variant='body14rg'
                    sx={{ color: "white" }}>
                    {QUESTION_TYPE_TITLE[type]}
                  </Typography>
                </Box>
                {(type === Question_Type.Practical || type === Question_Type.MultipleChoiceQuestions) && (
                  <Typography
                    variant='body14rg'
                    sx={{ color: "primary.main" }}>
                    {`Подтип: ${question.subtype}`}
                  </Typography>
                )}
              </Stack>
              <LabelBox text={QUESTION_LANGUAGE_TITLE[question.language]} />
            </Stack>
            {showConfirmationInfo && (
              <Chip
                size='small'
                label={isConfirm ? "Подтвержден" : "Не подтвержден"}
                sx={{
                  backgroundColor: `${confirmationColor(isConfirm)}.light`,
                  color: `${confirmationColor(isConfirm)}.contrast`,
                }}
              />
            )}
          </Stack>
          {/* { */}
          {/*  isConfirm && !isMicroCriterionExist && ( */}
          {/*    <Alert sx={{ maxWidth: 490 }}> */}
          {/*      Производится генерация микрокритериев, */}
          {/*      до&nbsp;момента окончания процесса вопрос */}
          {/*      не&nbsp;будет использоваться в&nbsp;тестировании. */}
          {/*    </Alert> */}
          {/*  ) */}
          {/* } */}
          <TextFormatter
            text={question.text}
            variant='body16rg'
          />
          {Boolean(questionParts?.length) && (
            <Stack spacing={1}>
              {questionParts?.map((part) => (
                <Stack
                  key={part.id}
                  direction='row'
                  alignItems='center'
                  spacing={0}>
                  <Checkbox
                    value={part.isCorrect}
                    disabled
                    sx={{ margin: 0 }}
                  />
                  <Typography>{part.content}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
        <Stack spacing={2}>
          <Stack
            direction='row'
            spacing={1.5}>
            {Boolean(questionCriterion.length) && (
              <Button
                variant='outlined'
                onClick={() => setСriterionOpened((prevState) => !prevState)}
                size='small'
                sx={{ fontSize: 14, px: 0.5 }}>
                {criterionButtonText}
              </Button>
            )}
            {question.referenceAnswer && (
              <Button
                variant='outlined'
                onClick={() => setReferenceAnswerOpened((prevState) => !prevState)}
                size='small'
                sx={{ fontSize: 14, px: 0.5 }}>
                {referenceAnswerButtonText}
              </Button>
            )}
            <Stack
              direction='row'
              spacing={1.5}>
              <Stack
                direction='row'
                spacing={1.5}
                sx={{ "& > div": { display: "inherit" } }}>
                <LabelBox text='Время ответа' />
                <ValueBox text={formatNumber(["минута", "минуты", "минут"], Math.ceil(question.time / 60))} />
              </Stack>
              <Stack
                direction='row'
                spacing={1.5}
                sx={{ "& > div": { display: "inherit" } }}>
                <LabelBox text='Рекомендуемое время ответа' />
                <ValueBox
                  text={formatNumber(["минута", "минуты", "минут"], Math.ceil((question.recommendedTime || 0) / 60))}
                />
              </Stack>
            </Stack>
          </Stack>
          {referenceAnswerOpened && (
            <Box
              sx={{
                padding: 1.5,
                backgroundColor: "green.light",
                borderLeft: "3px solid",
                borderColor: "green.contrast",
                borderTopRightRadius: 6,
                borderBottomRightRadius: 6,
              }}>
              <TextFormatter text={question.referenceAnswer!} />
            </Box>
          )}
        </Stack>
        {criterionOpened && (
          <Stack spacing={1}>
            {questionCriterion.map((criteria, index) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={`criteria-${index}`}
                sx={{
                  backgroundColor: "bgSwitch",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                }}>
                <Typography
                  variant='body14rg'
                  sx={{ color: "primary.dark" }}>
                  {criteria}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
        {isConfirm ? (
          <Button
            variant='outlined'
            onClick={onConfirm}
            disabled={selectionMode}
            sx={{ width: "fit-content" }}>
            Отклонить
          </Button>
        ) : (
          <Button
            variant='contained'
            onClick={onConfirm}
            disabled={selectionMode}
            sx={{ width: "fit-content" }}>
            Подтвердить
          </Button>
        )}
      </Stack>
    </Box>
  );
};
export default QuestionItem;
